import { useUnmount } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { type Editor } from '@meitu/saas-editor-core';
import { type TextEditorPlugin } from '@meitu/saas-editor-core/es/plugins/text-editor';
import { EditorContext } from '@/hooks/use-editor';
import { type ListStyleStruct } from '@/types';
import { VectorImagePanel, VectorTextPanel } from './panels';
import { type PanelTab, type WidgetCommonProps } from './types';
import { VectorPanelHeader } from './widgets';
import './style.less';
import './shared.less';
import { type FontItem } from '@meitu/xdesign-widgets';

type Props = {
    isIntl: boolean;
    fonts: FontItem[];
    editor: Editor;
    layerId: string;
    /**
     * 列表样式列表
     */
    listStyles: ListStyleStruct[];
    /**
     * 是否是 VIP 用户
     */
    isVIP?: boolean;
    /**
     * 打开 VIP 弹窗
     */
    onOpenVIPModal?: () => void;
    /**
     * 应用层作图记录 id，主要用于埋点
     */
    recordId?: number;
    /**
     * 是否启用远程搜索
     */
    enableRemoteSearch?: boolean;
};

export const VectorLayerPanel: React.FC<Props> = ({
    isIntl,
    fonts,
    editor,
    layerId,
    listStyles,
    recordId,
    isVIP,
    onOpenVIPModal,
    enableRemoteSearch,
}) => {
    const commonProps: WidgetCommonProps = {
        layerId,
        isIntl,
        recordId,
        isVIP,
        onOpenVIPModal,
    };

    const [currentTab, setCurrentTab] = useState<PanelTab>('image');

    const handleSwitchTab = (tab: PanelTab) => {
        if (tab === currentTab) return;

        if (tab === 'image') {
            const editingLayer = editor.getInstance<TextEditorPlugin>('TextEditorPlugin').getEditingLayer();
            if (editingLayer) editor.getInstance<TextEditorPlugin>('TextEditorPlugin').exit();
        }

        setCurrentTab(tab);
    };

    // HACK: 文字编辑层的时候有在 mousedown 事件阻止冒泡
    // 但是阻止冒泡之后，点击文字编辑框就会影响弹出层自动收起
    // 后尝试在进入编辑态时调用 disableMouseSelect，退出编辑时调用 enableMouseSelect 发现不生效（disableMouseSelect 后仍可以拉动选框）
    // 郑鹏反馈这个问题不太好改，暂时先在业务方进行 HACK
    // TODO: 无限画布新文字编辑版本待确认
    // useEffect(() => {
    //     const dispose = editor.getInstance<TextEditorPlugin>('TextEditorPlugin').onMouseDown(() => {
    //         const body = document.body;

    //         if (body) {
    //             const clickEvent = new MouseEvent('mousedown', {
    //                 bubbles: true,
    //                 cancelable: true,
    //                 view: window,
    //             });

    //             body.dispatchEvent(clickEvent);
    //         }
    //     });
    //     return () => dispose();
    // }, []);

    // 切换图层时重置 tab
    useEffect(() => {
        setCurrentTab('image');
    }, [layerId]);

    useUnmount(() => {
        const editingLayer = editor.getInstance<TextEditorPlugin>('TextEditorPlugin').getEditingLayer();
        if (editingLayer && editingLayer.id === layerId)
            editor.getInstance<TextEditorPlugin>('TextEditorPlugin').exit();
    });

    return (
        <EditorContext.Provider value={editor}>
            <div className="vector-layer-panel">
                <VectorPanelHeader
                    currentTab={currentTab}
                    onSwitchTab={handleSwitchTab}
                    layerId={layerId}
                    recordId={recordId}
                />

                <div className="vector-layer-panel__content">
                    {currentTab === 'image' ? (
                        <VectorImagePanel {...commonProps} />
                    ) : (
                        <VectorTextPanel
                            fonts={fonts}
                            listStyles={listStyles}
                            enableRemoteSearch={enableRemoteSearch}
                            {...commonProps}
                        />
                    )}
                </div>
            </div>
        </EditorContext.Provider>
    );
};
